<!--
  TokenEditor.vue -- contenteditable subject/body editor with contextual
  token pills (Phase 1 PR-4).

  Two variants via ``variant`` prop:
    - "subject": single-line (Enter suppressed), a trailing "Field" button.
    - "body": multi-line rich text (bold/italic/link/lists toolbar) + an
      "Insert field" button.

  Token behavior (LOAD-BEARING -- see tokenHtml.js):
    - Inserting a field drops a non-editable pill:
        <span class="tok" contenteditable="false" data-key="KEY">Label</span>
      showing the friendly LABEL, never raw {code}.
    - Backspace immediately before / inside a selected chip deletes the whole
      chip (a chip is one atomic object).
    - The editor emits the token-keyed HTML (chip form) on every change; the
      parent persists it verbatim (backend sanitizes + resolves it).

  contentEditable is seeded imperatively (via ``initialHtml`` + ``presetKey``)
  so Vue never fights the DOM. Changing ``presetKey`` re-seeds -- the parent
  bumps it when the active email type switches.

  a11y (ADR 0028): the editable region is a labeled textbox
  (role=textbox, aria-multiline, aria-label); chips carry aria-label and are
  keyboard-deletable via Backspace.
-->
<template>
  <div class="token-editor" :data-testid="`${testidPrefix}-root`">
    <!-- Body toolbar (rich text) -->
    <div
      v-if="variant === 'body'"
      class="token-editor__toolbar"
      :data-testid="`${testidPrefix}-toolbar`"
    >
      <button
        type="button"
        class="token-editor__tool"
        title="Bold"
        aria-label="Bold"
        :data-testid="`${testidPrefix}-bold`"
        @mousedown.prevent="exec('bold')"
      >
        <v-icon aria-hidden="true">format_bold</v-icon>
      </button>
      <button
        type="button"
        class="token-editor__tool"
        title="Italic"
        aria-label="Italic"
        :data-testid="`${testidPrefix}-italic`"
        @mousedown.prevent="exec('italic')"
      >
        <v-icon aria-hidden="true">format_italic</v-icon>
      </button>
      <button
        type="button"
        class="token-editor__tool"
        title="Link"
        aria-label="Insert link"
        :data-testid="`${testidPrefix}-link`"
        @mousedown.prevent="createLink"
      >
        <v-icon aria-hidden="true">link</v-icon>
      </button>
      <span class="token-editor__sep" />
      <button
        type="button"
        class="token-editor__tool"
        title="Bulleted list"
        aria-label="Bulleted list"
        :data-testid="`${testidPrefix}-ul`"
        @mousedown.prevent="exec('insertUnorderedList')"
      >
        <v-icon aria-hidden="true">format_list_bulleted</v-icon>
      </button>
      <button
        type="button"
        class="token-editor__tool"
        title="Numbered list"
        aria-label="Numbered list"
        :data-testid="`${testidPrefix}-ol`"
        @mousedown.prevent="exec('insertOrderedList')"
      >
        <v-icon aria-hidden="true">format_list_numbered</v-icon>
      </button>
      <button
        v-if="hasTokens"
        type="button"
        class="token-editor__insert"
        :disabled="disabled"
        :data-testid="`${testidPrefix}-insert`"
        aria-haspopup="listbox"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        @mousedown.prevent="toggleMenu"
      >
        <v-icon aria-hidden="true">add</v-icon>Insert field
      </button>
    </div>

    <!-- Subject: inline editor + trailing Field button -->
    <div
      v-if="variant === 'subject'"
      class="token-editor__subject-shell"
    >
      <div
        ref="editable"
        class="token-editor__editable token-editor__editable--subject"
        :contenteditable="!disabled"
        role="textbox"
        :aria-label="ariaLabel || 'Subject line'"
        :aria-multiline="false"
        :data-placeholder="placeholder"
        :data-testid="`${testidPrefix}-editable`"
        @input="emitChange"
        @blur="emitChange"
        @keydown="onKeydown"
      />
      <button
        v-if="hasTokens"
        type="button"
        class="token-editor__subject-insert"
        :disabled="disabled"
        :data-testid="`${testidPrefix}-insert`"
        aria-haspopup="listbox"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        @mousedown.prevent="toggleMenu"
      >
        <v-icon aria-hidden="true">add</v-icon>Field
      </button>
    </div>

    <!-- Body: multiline editable -->
    <div
      v-else-if="variant === 'body'"
      ref="editable"
      class="token-editor__editable token-editor__editable--body"
      :contenteditable="!disabled"
      role="textbox"
      :aria-label="ariaLabel || 'Message body'"
      :aria-multiline="true"
      :data-placeholder="placeholder"
      :data-testid="`${testidPrefix}-editable`"
      @input="emitChange"
      @blur="emitChange"
      @keydown="onKeydown"
    />

    <token-field-menu
      v-if="hasTokens && menuOpen"
      :tokens="tokens"
      :scope-label="scopeLabel"
      @pick="onPick"
      @close="menuOpen = false"
    />
  </div>
</template>

<script>
import TokenFieldMenu from "./TokenFieldMenu.vue";
import { chipHtml } from "./tokenHtml";

export default {
  name: "TokenEditor",

  components: { TokenFieldMenu },

  props: {
    // "subject" (single line) or "body" (rich text).
    variant: {
      type: String,
      default: "body",
      validator: (v) => ["subject", "body"].includes(v),
    },
    // Token-keyed HTML to seed the editor with.
    initialHtml: { type: String, default: "" },
    // Bump to force a re-seed (parent bumps on email-type switch).
    presetKey: { type: [String, Number], default: 0 },
    // [{ key, label, sample }] contextual tokens; empty => no Insert menu.
    tokens: { type: Array, default: () => [] },
    scopeLabel: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    ariaLabel: { type: String, default: "" },
    testidPrefix: { type: String, default: "token-editor" },
  },

  // ``change``: a USER edit (typing / chip insert / chip delete) -- the
  // parent debounces a save on these. ``seed``: a programmatic (re)seed of
  // the editor from stored/default copy -- the parent syncs its draft but
  // does NOT save, so merely viewing a type never writes to the server.
  emits: ["change", "seed"],

  data() {
    return { menuOpen: false };
  },

  computed: {
    hasTokens() {
      return Array.isArray(this.tokens) && this.tokens.length > 0;
    },
    labelOf() {
      const map = {};
      (this.tokens || []).forEach((t) => {
        map[t.key] = t.label;
      });
      return map;
    },
  },

  watch: {
    // Re-seed the contentEditable when the parent bumps presetKey.
    presetKey() {
      this.seed();
    },
  },

  mounted() {
    this.seed();
  },

  methods: {
    seed() {
      const el = this.$refs.editable;
      if (!el) return;
      el.innerHTML = this.initialHtml || "";
      // Re-stamp chip attributes lost to sanitize-on-write. The backend
      // sanitizer strips ``contenteditable`` from stored chip spans (only
      // ``class`` + ``data-key`` are allowlisted), so a re-loaded chip comes
      // back editable. Re-apply contenteditable="false" + aria-label so each
      // chip is again ONE atomic, keyboard-deletable, labeled object.
      el.querySelectorAll("[data-key]").forEach((chip) => {
        chip.setAttribute("contenteditable", "false");
        if (!chip.classList.contains("tok")) chip.classList.add("tok");
        if (!chip.getAttribute("aria-label")) {
          const key = chip.getAttribute("data-key");
          const label = this.labelOf[key] || chip.textContent || key;
          chip.setAttribute("aria-label", `${label} field`);
        }
      });
      // Programmatic seed -> ``seed`` (parent syncs draft, no save).
      this.$emit("seed", this.currentHtml());
    },

    currentHtml() {
      const el = this.$refs.editable;
      return el ? el.innerHTML : "";
    },

    emitChange() {
      this.$emit("change", this.currentHtml());
    },

    toggleMenu() {
      if (this.disabled) return;
      this.menuOpen = !this.menuOpen;
    },

    onKeydown(event) {
      // Single-line subject: suppress Enter.
      if (this.variant === "subject" && event.key === "Enter") {
        event.preventDefault();
        return;
      }
      if (event.key === "Backspace") {
        this.maybeDeleteChip(event);
      }
    },

    // Backspace deletes an adjacent/selected chip as one atomic unit.
    maybeDeleteChip(event) {
      const el = this.$refs.editable;
      if (!el) return;
      const sel = window.getSelection && window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);

      // Case 1: a chip (or nodes including one) is selected -> let default
      // deletion remove it, but ensure we don't leave a partial chip.
      if (!range.collapsed) {
        return; // default behavior removes the selection wholesale
      }

      // Case 2: caret is collapsed. Find the node immediately before it.
      const chip = this.chipBeforeCaret(range);
      if (chip) {
        event.preventDefault();
        chip.remove();
        this.emitChange();
      }
    },

    // Return the token chip element immediately before the collapsed caret,
    // or null. Handles the caret being at the start of a text node that
    // directly follows a chip, and the caret being directly after a chip.
    chipBeforeCaret(range) {
      const container = range.startContainer;
      const offset = range.startOffset;

      const isChip = (node) =>
        node &&
        node.nodeType === 1 &&
        node.hasAttribute &&
        node.hasAttribute("data-key");

      if (container.nodeType === 3) {
        // Text node: only when caret is at its very start do we look at the
        // previous sibling.
        if (offset === 0) {
          const prev = container.previousSibling;
          if (isChip(prev)) return prev;
        }
        return null;
      }
      // Element container: the node before the caret is childNodes[offset-1].
      if (offset > 0) {
        const prev = container.childNodes[offset - 1];
        if (isChip(prev)) return prev;
      }
      return null;
    },

    onPick(key) {
      this.menuOpen = false;
      this.insertChip(key);
    },

    // Insert a token chip at the caret (or at the end if the selection is
    // outside the editor). Emits the updated token-keyed HTML.
    insertChip(key) {
      const el = this.$refs.editable;
      if (!el) return;
      const label = this.labelOf[key] || key;

      const chip = document.createElement("span");
      chip.className = "tok";
      chip.setAttribute("contenteditable", "false");
      chip.setAttribute("data-key", key);
      chip.setAttribute("aria-label", `${label} field`);
      chip.textContent = label;

      const sel = window.getSelection && window.getSelection();
      let range;
      if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
        range = sel.getRangeAt(0);
        range.deleteContents();
      } else {
        range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
      }
      range.insertNode(chip);

      // Trailing space so the caret can sit after the chip and further
      // typing is not absorbed into it.
      const space = document.createTextNode(" ");
      if (chip.parentNode) {
        chip.parentNode.insertBefore(space, chip.nextSibling);
      }
      if (sel) {
        const after = document.createRange();
        after.setStartAfter(space);
        after.collapse(true);
        sel.removeAllRanges();
        sel.addRange(after);
      }
      this.emitChange();
    },

    // Rich-text command. document.execCommand is deprecated but remains the
    // pragmatic contentEditable formatting primitive; jsdom lacks it, so we
    // guard for tests.
    exec(command, value) {
      const el = this.$refs.editable;
      if (!el || this.disabled) return;
      el.focus();
      if (typeof document.execCommand === "function") {
        try {
          document.execCommand(command, false, value);
        } catch (err) {
          // jsdom / unsupported command -- no-op, do not crash the editor.
        }
      }
      this.emitChange();
    },

    createLink() {
      const url = "https://";
      this.exec("createLink", url);
    },

    // Focus helper (parent may call to refocus after external actions).
    focus() {
      const el = this.$refs.editable;
      if (el) el.focus();
    },
  },
};
</script>

<style scoped>
.token-editor {
  position: relative;
}
.token-editor__toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 6px;
  border: 1px solid var(--tf-border, #cfd8e3);
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  background: var(--tf-bg-subtle, #f8fafc);
}
.token-editor__tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--tf-fg-3, #546e7a);
  cursor: pointer;
}
.token-editor__tool:hover {
  background: var(--tf-primary-soft, #e3f2fd);
  color: var(--tf-primary, #119ae9);
}
.token-editor__sep {
  width: 1px;
  height: 20px;
  margin: 0 4px;
  background: var(--tf-border, #cfd8e3);
}
.token-editor__insert,
.token-editor__subject-insert {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  padding: 4px 10px;
  border: none;
  border-radius: 9999px;
  background: var(--tf-primary-soft, #e3f2fd);
  color: var(--tf-primary, #119ae9);
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
}
.token-editor__insert:disabled,
.token-editor__subject-insert:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.token-editor__subject-shell {
  display: flex;
  align-items: center;
  border: 1px solid var(--tf-border, #cfd8e3);
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.token-editor__subject-insert {
  flex-shrink: 0;
  margin: 4px 6px 4px 0;
}
.token-editor__editable {
  outline: none;
  font-size: 15px;
  color: var(--tf-fg-1, #133752);
  line-height: 1.55;
}
.token-editor__editable--subject {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  white-space: nowrap;
  overflow-x: auto;
}
.token-editor__editable--body {
  min-height: 150px;
  padding: 12px 14px;
  border: 1px solid var(--tf-border, #cfd8e3);
  border-radius: 0 0 8px 8px;
  background: #fff;
}
.token-editor__editable:empty::before {
  content: attr(data-placeholder);
  color: var(--tf-fg-4, #90a4ae);
}
/* Token pill styling inside the editor. Not scoped-able to injected
   contenteditable children, so use :deep. */
.token-editor__editable :deep(.tok) {
  display: inline-block;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--tf-token-fg, #2456b8);
  background: var(--tf-token-bg, #eef4ff);
  border: 1px solid var(--tf-token-border, #cfe0fb);
  border-radius: 6px;
  padding: 0 6px;
  margin: 0 1px;
  white-space: nowrap;
  user-select: all;
}
</style>
